import { useState } from "react";
import { opsAPI } from "./opsApi";
import { toast } from "sonner";
import {
  Sparkles, AlertTriangle, ShieldAlert, Zap, Users, DollarSign, Bed,
  Wrench, Package, Info, ChevronDown, Check, X, Clock, ThumbsUp,
} from "lucide-react";

const MODULE_ICON = {
  fo: Bed, hk: Sparkles, eng: Wrench, engineering: Wrench,
  inv: Package, inventory: Package, wf: Users, workforce: Users,
  revenue: DollarSign, fin: DollarSign, ops: ShieldAlert,
  default: Info,
};
const SEV_COLOR = {
  critical: "bg-red-500/25 text-red-100 border-red-500/60 shadow-[0_0_20px_rgba(244,63,94,0.25)]",
  high:     "bg-amber-500/25 text-amber-100 border-amber-500/60",
  medium:   "bg-sky-500/25 text-sky-100 border-sky-500/60",
  low:      "bg-emerald-500/25 text-emerald-100 border-emerald-500/60",
  info:     "bg-white/10 text-white/80 border-white/25",
};
const SEV_LABEL = { critical: "Critical", high: "High", medium: "Medium", low: "Low", info: "Info" };

export default function InsightsTimeline({ insights = [], onChanged }) {
  const [expanded, setExpanded] = useState(null);

  if (!insights.length) {
    return (
      <div className="p-8 text-center">
        <Sparkles className="w-8 h-8 text-emerald-400 mx-auto mb-3" strokeWidth={1.8}/>
        <div className="text-white font-semibold">All clear.</div>
        <div className="text-[13px] text-white/70 mt-1">The rule engine found no open issues right now.</div>
      </div>
    );
  }

  const act = async (id, action) => {
    try {
      await opsAPI.insightAction(id, action);
      toast.success(`Insight ${action}d`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div className="space-y-2" data-testid="insights-timeline">
      {insights.map((i) => {
        const Icon = MODULE_ICON[i.module] || MODULE_ICON.default;
        const open = expanded === i.id;
        return (
          <div
            key={i.id}
            data-testid={`insight-${i.id}`}
            className={`group relative bg-white/[0.05] hover:bg-white/[0.10] border border-white/[0.14] rounded-2xl transition-all overflow-hidden ${i.severity === "critical" ? "ring-1 ring-red-500/40" : ""}`}
          >
            <button onClick={() => setExpanded(open ? null : i.id)} className="w-full text-left px-4 py-3.5 flex items-start gap-4">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${SEV_COLOR[i.severity] || SEV_COLOR.info}`}>
                <Icon className="w-4 h-4" strokeWidth={2}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-white font-semibold truncate">{i.title}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-md border ${SEV_COLOR[i.severity] || SEV_COLOR.info} uppercase tracking-wider font-semibold`}>{SEV_LABEL[i.severity]}</span>
                </div>
                <div className="text-[12.5px] text-white/80 line-clamp-1">{i.reasoning}</div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-white/60">
                  <span className="uppercase tracking-wider">{i.module}</span>
                  <span>·</span>
                  <span className="tabular-nums">Confidence {Math.round((i.confidence || 0) * 100)}%</span>
                  {i.impact_currency ? (<><span>·</span><span className="text-emerald-300">Impact ~ ₹{Math.round(i.impact_currency).toLocaleString("en-IN")}</span></>) : null}
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 text-white/60 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}/>
            </button>

            {open && (
              <div className="px-4 pb-4 pt-1 border-t border-white/[0.10] space-y-3">
                <div className="text-[13px] text-white leading-relaxed">{i.reasoning}</div>
                {i.recommendation && (
                  <div className="rounded-lg bg-emerald-500/15 border border-emerald-500/40 px-3 py-2.5">
                    <div className="text-[10.5px] uppercase tracking-widest text-emerald-200 font-semibold mb-0.5">Recommended</div>
                    <div className="text-[13px] text-white">{i.recommendation}</div>
                  </div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => act(i.id, "acknowledge")} data-testid={`insight-ack-${i.id}`}
                    className="h-8 px-3 rounded-lg bg-emerald-500/25 text-emerald-100 hover:bg-emerald-500/35 text-[12px] font-semibold inline-flex items-center gap-1.5 border border-emerald-500/40">
                    <ThumbsUp className="w-3.5 h-3.5"/> Acknowledge
                  </button>
                  <button onClick={() => act(i.id, "snooze")} data-testid={`insight-snooze-${i.id}`}
                    className="h-8 px-3 rounded-lg bg-white/10 text-white hover:bg-white/20 text-[12px] font-semibold inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5"/> Snooze
                  </button>
                  <button onClick={() => act(i.id, "dismiss")} data-testid={`insight-dismiss-${i.id}`}
                    className="h-8 px-3 rounded-lg text-white/80 hover:text-white hover:bg-white/10 text-[12px] font-semibold inline-flex items-center gap-1.5 ml-auto">
                    <X className="w-3.5 h-3.5"/> Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
